import React from 'react';
import styled, { DefaultTheme, css } from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { StackPanel } from '../../stack-panel';
import { Typography } from '../../typography';
import { PopupMessageType } from '../popup-message-props';
import { selectThemeSemantics } from '../../../styling';
import { PopupMessageTypes } from '../theme';
import { IIllustrationProps, Illustration, IllustrationName } from '../../illustration';

interface IPopupMessageContentProps {
    illustrationName?: IllustrationName;
    message?: string;
    title?: string;
    type: PopupMessageType;
    onRenderContent(): React.ReactNode;
}

const getContentStylesByType = memoizeFunction((type: PopupMessageTypes) => {
    const getStyles = (theme: DefaultTheme) => {
        const { popupMessage: { colors } } = theme;
        return colors[type];
    };
    return css`
        border-bottom-color: ${({ theme }) => getStyles(theme).contentSeparatorColor};
        color: ${({ theme }) => getStyles(theme).contentColor};
    `;
});

function getTitleColorByType($type: PopupMessageType, theme: DefaultTheme) {
    const { popupMessage: { colors } } = theme;
    switch ($type) {
        case 'error':
            return colors.error.titleColor;
        case 'information':
            return colors.information.titleColor;
        case 'success':
            return colors.success.titleColor;
        case 'warning':
            return colors.warning.titleColor;
        default:
            return colors.information.titleColor;
    }
}

const Title = styled(Typography.H4) <{ $type: PopupMessageType }>`
    text-align: center;
    color: ${({ theme, $type }) => getTitleColorByType($type, theme)};
`;

const Content = styled(Typography.Small) <{ $type: PopupMessageType }>`
    ${({ $type }) => getContentStylesByType($type)};
    width: 100%;
    border-bottom-width: ${selectThemeSemantics(({ gap }) => gap.xs)}; 
    border-bottom-style: solid;
    padding-bottom: 40px;
    text-align: center;
    max-width: 500px;
    max-height: 100px;
    overflow-y: auto;
    box-sizing: border-box;
`;

function getIllustrationKey(type: PopupMessageType): IIllustrationProps['type'] {
    switch (type) {
        case 'error':
            return 'ap-error-illustration-new';
        case 'information':
            return 'ap-info-illustration-new';
        case 'success':
            return 'ap-success-illustration-new';
        case 'warning':
            return 'ap-warning-illustration-new';
        default:
            return 'ap-info-illustration-new';
    }
}

export const PopupMessageContent = React.memo((props: IPopupMessageContentProps) => {
    const { title, onRenderContent, type, message, illustrationName } = props;
    const illustrationKey = React.useMemo(() => illustrationName || getIllustrationKey(type), [illustrationName, type]);
    return (
        <StackPanel width="100%" gap="20" orientation="vertical" alignment="center">
            <Illustration type={illustrationKey} width="63px" />
            <StackPanel width="100%" gap="8" orientation="vertical" alignment="center">
                <Title $type={type}>{title}</Title>
                <Content $type={type}>{onRenderContent !== undefined ? onRenderContent() : message}</Content>
            </StackPanel>
        </StackPanel>
    );
});

PopupMessageContent.displayName = 'PopupMessageContent';
